/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import type { Meta, ApiListResponse } from '@mairistem/administres-utils';
import { request, buildEndPoint } from '@mairistem/tools';

import { BASE_URL_ETATCIVIL } from '@/constants';
import type { Acte, TypeActe } from '@/types';
import { toast } from 'react-toastify';

/**
 * Get the list of actes of the given type.
 *
 * Usage is get everything that the hook returns, call the get method with the params you want
 * (likely at least `page`) and then simply use the data, meta or loading.
 */
const useActes = (typeActe: TypeActe) => {
  const [loading, setLoading] = React.useState(false);
  const [datas, setDatas] = React.useState<Record<number, Array<Acte>>>({});
  const [metas, setMetas] = React.useState<Meta | null>(null);

  const reset = React.useCallback(() => {
    setDatas({});
    setMetas(null);
    setLoading(false);
  }, []);

  const get = React.useCallback(
    (params?: Record<string, unknown>) => {
      setLoading(true);

      const filteredParams = Object.fromEntries(
        Object.entries(params || {}).filter(([, value]) => value !== ''),
      );

      const req = request.get<unknown, ApiListResponse<Acte>>(
        buildEndPoint(
          `${BASE_URL_ETATCIVIL}/actes-${typeActe}`,
          filteredParams,
        ),
      );

      req
        .then(({ data, meta }) => {
          setDatas((prev) => ({
            ...prev,
            [meta.currentPage]: data,
          }));
          setMetas(meta);
        })
        .catch(() => {
          toast('Une erreur est survenue lors du chargement des actes', {
            type: 'error',
          });
        })
        .finally(() => {
          setLoading(false);
        });

      return req;
    },
    [typeActe],
  );

  return {
    data: React.useMemo(() => _.flatten(_.values(datas)), [datas]),
    meta: metas,
    loading,
    get,
    reset,
  };
};

export default useActes;
